import type { BehaviorGraph } from "../layout/types.js";
import type {
  PathEntry,
  PathEntryValueType,
} from "../layout/data-source-schema.js";
import {
  AGE_CATEGORY_FIELDS,
  ORGANISATION_PATHS,
  WEIGHT_CATEGORY_FIELDS,
} from "../layout/data-source-schema.js";
import { synchronizeBehaviorObjectPaths } from "../blueprint/object-path-typing.js";
import {
  CUSTOM_DISCIPLINE_ATHLETE_CUSTOM_GROUP_ID_KEY,
  CUSTOM_DISCIPLINE_ATHLETE_GROUP_ID_KEY,
  CUSTOM_DISCIPLINE_ATHLETE_ID_KEY,
  CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY,
} from "./records.js";
import {
  CUSTOM_DISCIPLINE_AGE_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_SPORT_AGE_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_SPORT_WEIGHT_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETES_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_MEET_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE,
  CUSTOM_DISCIPLINE_ORGANISATION_SOURCE,
  CUSTOM_DISCIPLINE_ORGANISATIONS_SOURCE,
  CUSTOM_DISCIPLINE_DISCIPLINES_SOURCE,
  CUSTOM_DISCIPLINE_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_GROUPS_SOURCE,
  CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE,
  CUSTOM_DISCIPLINE_LEAGUES_SOURCE,
  CUSTOM_DISCIPLINE_LEVELS_SOURCE,
  CUSTOM_DISCIPLINE_RECORDS_SOURCE,
  CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE,
  CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
  CUSTOM_DISCIPLINE_WEIGHT_CATEGORIES_SOURCE,
} from "./sources.js";

/**
 * Athlete field used to type `get` paths in the custom-discipline editor.
 */
export type CustomDisciplinePathField = {
  key: string;
  type: string;
  referenceSource?: string | null;
  referenceIdPath?: string | null;
  referenceLabelPath?: string | null;
};

/**
 * Custom discipline modal input used to type `inputValues` in custom modal
 * behavior graphs.
 */
export type CustomDisciplineModalInputPathField = {
  key: string;
  label?: string | null;
  type?: string | null;
};

const REFERENCE_SOURCE_PATHS: Record<string, PathEntry[]> = {
  groups: [
    { path: "categoryDisciplineId", valueType: "number" },
    { path: "id", valueType: "string" },
    { path: "name", valueType: "string" },
    { path: "plateauNumber", valueType: "number" },
    { path: "groupNumber", valueType: "number" },
    { path: "competitionFormat", valueType: "string" },
    { path: "competitionFormatRaw", valueType: "string" },
    { path: "customDisciplineGroupId", valueType: "string" },
    { path: "customDisciplineGroupName", valueType: "string" },
    { path: "groupTypeId", valueType: "string" },
    { path: "groupTypeName", valueType: "string" },
    { path: "groupTypeCode", valueType: "string" },
    { path: "disciplineId", valueType: "string" },
    { path: "disciplineName", valueType: "string" },
    { path: "disciplineCode", valueType: "string" },
  ],
  disciplineAgeCategories: AGE_CATEGORY_FIELDS,
  disciplineWeightCategories: WEIGHT_CATEGORY_FIELDS,
  ageCategories: AGE_CATEGORY_FIELDS,
  weightCategories: WEIGHT_CATEGORY_FIELDS,
  disciplines: [
    { path: "id", valueType: "number" },
    { path: "name", valueType: "string" },
    { path: "code", valueType: "string" },
    { path: "competitionFormat", valueType: "string" },
  ],
  levels: [
    { path: "id", valueType: "number" },
    { path: "name", valueType: "string" },
    { path: "minTotal", valueType: "number" },
    { path: "maxTotal", valueType: "number" },
    { path: "ageCategoryId", valueType: "number" },
    { path: "weightCategoryId", valueType: "number" },
    { path: "disciplineId", valueType: "number" },
  ],
  records: [
    { path: "id", valueType: "number" },
    { path: "movement", valueType: "string" },
    { path: "weight", valueType: "number" },
    { path: "ageCategoryId", valueType: "number" },
    { path: "weightCategoryId", valueType: "number" },
    { path: "dateLocation", valueType: "string" },
    { path: "athleteName", valueType: "string" },
    { path: "disciplineId", valueType: "number" },
    { path: "sex", valueType: "string" },
    { path: "organisationId", valueType: "string" },
    { path: "organisation", valueType: "object", children: ORGANISATION_PATHS },
  ],
  leagues: [
    { path: "id", valueType: "number" },
    { path: "name", valueType: "string" },
  ],
};

/** Shared group identity; discipline-specific fields come from GROUP definitions. */
const CUSTOM_GROUP_PATHS: PathEntry[] = [
  { path: "categoryDisciplineId", valueType: "number" },
  { path: "id", valueType: "string" },
  { path: "nom", valueType: "string" },
  { path: "name", valueType: "string" },
  { path: "meet_id", valueType: "number" },
  { path: "customDisciplineGroupId", valueType: "string" },
  { path: "plateauNumber", valueType: "number" },
  { path: "groupNumber", valueType: "number" },
  { path: "competitionFormat", valueType: "string" },
  { path: "competitionFormatRaw", valueType: "string" },
  { path: "customDisciplineGroupName", valueType: "string" },
  { path: "groupTypeId", valueType: "string" },
  { path: "groupTypeName", valueType: "string" },
  { path: "groupTypeCode", valueType: "string" },
  { path: "disciplineId", valueType: "string" },
  { path: "disciplineName", valueType: "string" },
  { path: "disciplineCode", valueType: "string" },
];

const CUSTOM_MEET_PATHS: PathEntry[] = [
  { path: "id", valueType: "number" },
  { path: "organisationId", valueType: "string" },
  { path: "nom", valueType: "string" },
  { path: "name", valueType: "string" },
  { path: "date", valueType: "date" },
  { path: "startdate", valueType: "date" },
  { path: "enddate", valueType: "date" },
  { path: "lieu", valueType: "string" },
  { path: "location", valueType: "string" },
];

const CUSTOM_DISCIPLINE_RUNTIME_SOURCE_PATHS: Record<
  string,
  PathEntry[] | null
> = {
  [CUSTOM_DISCIPLINE_SPORT_AGE_CATEGORIES_SOURCE]:
    REFERENCE_SOURCE_PATHS.disciplineAgeCategories,
  [CUSTOM_DISCIPLINE_SPORT_WEIGHT_CATEGORIES_SOURCE]:
    REFERENCE_SOURCE_PATHS.disciplineWeightCategories,
  displaySettings: [
    { path: "lightsEnabled", valueType: "boolean" },
    { path: "automaticRefereeing", valueType: "boolean" },
  ],
  [CUSTOM_DISCIPLINE_CURRENT_MEET_SOURCE]: CUSTOM_MEET_PATHS,
  [CUSTOM_DISCIPLINE_ORGANISATION_SOURCE]: ORGANISATION_PATHS,
  [CUSTOM_DISCIPLINE_ORGANISATIONS_SOURCE]: ORGANISATION_PATHS,
  [CUSTOM_DISCIPLINE_GROUP_SOURCE]: CUSTOM_GROUP_PATHS,
  [CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE]: CUSTOM_GROUP_PATHS,
  [CUSTOM_DISCIPLINE_GROUPS_SOURCE]: CUSTOM_GROUP_PATHS,
  [CUSTOM_DISCIPLINE_RECORDS_SOURCE]: REFERENCE_SOURCE_PATHS.records ?? [],
  [CUSTOM_DISCIPLINE_LEVELS_SOURCE]: REFERENCE_SOURCE_PATHS.levels ?? [],
  [CUSTOM_DISCIPLINE_AGE_CATEGORIES_SOURCE]:
    REFERENCE_SOURCE_PATHS.ageCategories ?? [],
  [CUSTOM_DISCIPLINE_WEIGHT_CATEGORIES_SOURCE]:
    REFERENCE_SOURCE_PATHS.weightCategories ?? [],
  [CUSTOM_DISCIPLINE_DISCIPLINES_SOURCE]:
    REFERENCE_SOURCE_PATHS.disciplines ?? [],
  [CUSTOM_DISCIPLINE_LEAGUES_SOURCE]: REFERENCE_SOURCE_PATHS.leagues ?? [],
  [CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE]: null,
  [CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE]: [
    {
      path: "result",
      children: [
        { path: "referee_type", valueType: "string" },
        { path: "passed", valueType: "number" },
        { path: "color", valueType: "string" },
        { path: "juryEnabled", valueType: "number" },
      ],
    },
  ],
  [CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE]: null,
  [CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE]: null,
};

function appendUniquePath(entries: PathEntry[], entry: PathEntry): PathEntry[] {
  return entries.some((candidate) => candidate.path === entry.path)
    ? entries
    : [...entries, entry];
}

function referenceChildrenForField(
  field: CustomDisciplinePathField,
  groupPaths?: PathEntry[],
): PathEntry[] {
  const sourceChildren = field.referenceSource
    ? field.referenceSource === "groups" && groupPaths
      ? groupPaths
      : (REFERENCE_SOURCE_PATHS[field.referenceSource] ?? [])
    : [];
  return [field.referenceIdPath, field.referenceLabelPath]
    .map((path) => path?.trim())
    .filter((path): path is string => Boolean(path))
    .reduce(
      (children, path) =>
        appendUniquePath(children, { path, valueType: "string" }),
      sourceChildren,
    );
}

function pathEntriesForField(
  field: CustomDisciplinePathField,
  groupPaths?: PathEntry[],
): PathEntry[] {
  const key = field.key.trim();
  if (!key || key === CUSTOM_DISCIPLINE_ATHLETE_ID_KEY) {
    return [];
  }
  if (field.type === "FOREIGN_KEY") {
    return [
      { path: `${key}Id`, valueType: "string" },
      {
        path: key,
        valueType: "object",
        children: referenceChildrenForField(field, groupPaths),
      },
    ];
  }
  return [
    {
      path: key,
      valueType: customDisciplineFieldTypeToPathValueType(field.type),
    },
  ];
}

/**
 * Maps a custom-discipline field type to the Behavior `get` output port type.
 *
 * LIFT stores a numeric weight like NUMBER; SELECT stores its string value.
 *
 * @param type - Field definition type (`TEXT`, `NUMBER`, `BOOLEAN`, …).
 * @returns Path entry value type for the `get` operator.
 */
export function customDisciplineFieldTypeToPathValueType(
  type: string,
): Exclude<PathEntryValueType, "object"> {
  switch (type) {
    case "NUMBER":
    case "LIFT":
      return "number";
    case "BOOLEAN":
      return "boolean";
    case "DATE":
      return "date";
    case "TEXT":
    case "SELECT":
    default:
      return "string";
  }
}

/**
 * Builds the `get` dropdown schemas for custom-discipline athletes, groups and references.
 *
 * @param fields - Group-type ATHLETE fields (`key` + `type`).
 * @param options.referenceItemSource - `referenceSource` of the `FOREIGN_KEY`
 * field being edited (`referenceFilterBehavior` graphs only); when provided,
 * types the `referenceItem` variable's `get` fields after that reference list
 * (e.g. `ageCategories` rows).
 * @param options.groupFields - Configured GROUP fields, shown before shared metadata.
 * @returns Overlay consumed by BehaviorCanvas `customObjectPathSchema`.
 */
export function buildCustomDisciplineObjectPathSchema(
  fields: readonly CustomDisciplinePathField[],
  options?: {
    referenceItemSource?: string | null;
    modalInputFields?: readonly CustomDisciplineModalInputPathField[];
    groupFields?: readonly CustomDisciplinePathField[];
  },
): Record<string, PathEntry[] | null> {
  const groupPathsByKey = new Map(
    (options?.groupFields ?? [])
      .flatMap((field) => pathEntriesForField(field))
      .map((entry) => [entry.path, entry]),
  );
  for (const entry of CUSTOM_GROUP_PATHS) {
    if (!groupPathsByKey.has(entry.path)) {
      groupPathsByKey.set(entry.path, entry);
    }
  }
  const groupPaths = [...groupPathsByKey.values()];
  const paths: PathEntry[] = [
    { path: CUSTOM_DISCIPLINE_ATHLETE_ID_KEY, valueType: "string" },
    { path: CUSTOM_DISCIPLINE_ATHLETE_GROUP_ID_KEY, valueType: "string" },
    {
      path: CUSTOM_DISCIPLINE_ATHLETE_CUSTOM_GROUP_ID_KEY,
      valueType: "string",
    },
    { path: CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY, valueType: "number" },
    ...fields.flatMap((field) => pathEntriesForField(field, groupPaths)),
  ];
  const referenceItemPaths = options?.referenceItemSource
    ? options.referenceItemSource === "groups"
      ? groupPaths
      : (REFERENCE_SOURCE_PATHS[options.referenceItemSource] ?? null)
    : null;
  const modalInputPaths: PathEntry[] =
    options?.modalInputFields?.flatMap((field) => {
      const key = field.key.trim();
      if (!key) {
        return [];
      }
      const label = field.label?.trim() || key;
      const valueType = customDisciplineFieldTypeToPathValueType(
        field.type ?? "TEXT",
      );
      const paths: PathEntry[] = [{ path: key, label, valueType }];
      if (field.type === "SELECT" || field.type === "FOREIGN_KEY") {
        paths.push({
          path: `${key}Id`,
          label: `${label} ID`,
          valueType: "string",
        });
        paths.push({
          path: `${key}Label`,
          label: `${label} label`,
          valueType: "string",
        });
      }
      return paths;
    }) ?? [];
  modalInputPaths.push(
    {
      path: "__formMode",
      label: "Mode du formulaire (create / edit)",
      valueType: "string",
    },
    {
      path: "__today",
      label: "Date du jour à l’ouverture du formulaire",
      valueType: "date",
    },
  );
  return {
    [CUSTOM_DISCIPLINE_ATHLETE_SOURCE]: paths,
    [CUSTOM_DISCIPLINE_ATHLETES_SOURCE]: paths,
    [CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE]: null,
    ...CUSTOM_DISCIPLINE_RUNTIME_SOURCE_PATHS,
    currentAthlete: paths,
    group: groupPaths,
    currentGroup: groupPaths,
    groups: groupPaths,
    ...(modalInputPaths.length > 0
      ? {
          [CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE]: modalInputPaths,
        }
      : {}),
    ...(referenceItemPaths
      ? { [CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE]: referenceItemPaths }
      : {}),
  };
}

/**
 * Restores Get sources, selected paths and port types from the custom schema and graph connections.
 *
 * List item access and nested Get chains are resolved upstream first, including
 * older graphs with missing or stale object-source metadata.
 *
 * @param graph - Behavior graph to normalize.
 * @param schema - Overlay from {@link buildCustomDisciplineObjectPathSchema}.
 * @returns The graph with corrected metadata, preserving unchanged node references.
 */
export function applyCustomDisciplineGetOutputTypes(
  graph: BehaviorGraph,
  schema: Record<string, PathEntry[] | null>,
): BehaviorGraph {
  return synchronizeBehaviorObjectPaths(graph, schema);
}

/**
 * Parses a persisted custom-discipline behavior payload (object or JSON string).
 *
 * @param raw - Field `behavior` from API/SQLite.
 * @returns A graph with nodes/connections, or null when missing/invalid.
 */
export function parseCustomDisciplineBehavior(
  raw: unknown,
): BehaviorGraph | null {
  if (raw == null || raw === "") {
    return null;
  }
  let payload: unknown = raw;
  if (typeof raw === "string") {
    try {
      payload = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const candidate = payload as {
    version?: unknown;
    graph?: { nodes?: unknown; connections?: unknown };
    nodes?: unknown;
    connections?: unknown;
  };
  const graph = candidate.graph ?? candidate;
  if (
    !graph ||
    typeof graph !== "object" ||
    !Array.isArray((graph as { nodes?: unknown }).nodes) ||
    !Array.isArray((graph as { connections?: unknown }).connections)
  ) {
    return null;
  }
  const nodes = (graph as BehaviorGraph).nodes;
  const connections = (graph as BehaviorGraph).connections;
  if (nodes.length === 0) {
    return null;
  }
  return { nodes, connections };
}
